import { useCallback, useSyncExternalStore } from 'react';
import { useMediaQuery } from '../ui/hooks';

/**
 * Exactly two modes. `null` (no explicit choice) follows the OS preference,
 * which itself resolves to one of these two.
 */
export type ThemeName = 'light' | 'dark';
export type ThemeChoice = ThemeName | null;

export const THEME_NAMES: readonly ThemeName[] = ['light', 'dark'];

export const THEME_STORAGE_KEY = 'ithelpdesk.theme';

/** A previously supported mode that was removed; stored values of it fall back to Light. */
const LEGACY_AZURE = 'azure';

/**
 * One module-level store so every consumer (top-bar toggle, user menu, …)
 * sees the same choice — two independent `useState`s used to drift apart.
 * Persisted as JSON in localStorage (same format the inline pre-paint script
 * in index.html reads, so there is no wrong-theme flash on load). Every
 * storage access is guarded: private mode / blocked storage just means the
 * choice lasts for this page only.
 */
function readStored(): ThemeChoice {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    const v = raw === null ? null : (JSON.parse(raw) as unknown);
    if (v === 'light' || v === 'dark') return v;
    // Legacy: the retired "azure" mode maps to Light and is rewritten once, so
    // the stored value is valid from now on. Anything else follows the OS.
    if (v === LEGACY_AZURE) {
      window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify('light'));
      return 'light';
    }
    return null;
  } catch {
    return null;
  }
}

let current: ThemeChoice = typeof window === 'undefined' ? null : readStored();
const listeners = new Set<() => void>();

function applyToDocument(choice: ThemeChoice): void {
  try {
    const root = document.documentElement;
    if (choice) root.setAttribute('data-theme', choice);
    else root.removeAttribute('data-theme');
  } catch {
    // Non-browser test environment — theme follows the media query.
  }
}

export function setThemeChoice(choice: ThemeChoice): void {
  current = choice;
  try {
    if (choice) window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(choice));
    else window.localStorage.removeItem(THEME_STORAGE_KEY);
  } catch {
    // Storage unavailable — still applied for this session.
  }
  applyToDocument(choice);
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab changed the theme: follow it.
  const onStorage = (e: StorageEvent): void => {
    if (e.key !== THEME_STORAGE_KEY) return;
    current = readStored();
    applyToDocument(current);
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Backs every theme toggle. An explicit choice is written to `[data-theme]`
 * on `<html>`, which `tokens.css` gives priority over `prefers-color-scheme`.
 * With no explicit choice the attribute is removed so the OS preference —
 * and any later OS-level change — keeps driving the theme live.
 */
export function useTheme(): { effective: ThemeName; choice: ThemeChoice; toggle: () => void; setChoice: (choice: ThemeChoice) => void } {
  const choice = useSyncExternalStore(subscribe, () => current, () => null);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const effective: ThemeName = choice ?? (prefersDark ? 'dark' : 'light');

  const toggle = useCallback(() => {
    setThemeChoice(effective === 'dark' ? 'light' : 'dark');
  }, [effective]);

  return { effective, choice, toggle, setChoice: setThemeChoice };
}
